import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/App';

/* ─── Tour step definitions ──────────────────────────────── */
const TOUR_STEPS = {
  admin: [
    {
      target: null,
      title: 'Welcome to HiroJet OS',
      description: 'Your all-in-one AI-powered Applicant Tracking System. Built for teams that hire at scale. Let\'s take a quick tour of everything you can do as an Admin.',
      icon: '🚀',
    },
    {
      target: '[data-testid="nav-dashboard"]',
      title: 'Dashboard',
      description: 'Your command centre. See team-wide KPIs, recruiter leaderboard, target progress, and AI motivation — all at a glance. Check it every morning to stay on top.',
      icon: '📊',
    },
    {
      target: '[data-testid="nav-jobs"]',
      title: 'Jobs',
      description: 'Create, manage and share job listings. Each job has a Kanban pipeline where you can track every candidate from "Internal Screening" to "Joined". Assign recruiters to each job.',
      icon: '💼',
    },
    {
      target: '[data-testid="nav-talent-hub"]',
      title: 'Talent Hub',
      description: 'Your entire candidate database. Upload resumes in bulk, search by skills or name, and manage candidate profiles. Every resume is AI-parsed automatically.',
      icon: '👥',
    },
    {
      target: '[data-testid="nav-ai-recruiter"]',
      title: 'AI Recruiter',
      description: 'Let AI do the heavy lifting. Source, screen, and rank candidates using GPT-4o. Choose from 5 modes: Quick Screen, Deep Dive, Culture Fit, Technical Assessment, and Executive Search.',
      icon: '🤖',
    },
    {
      target: '[data-testid="nav-reachout"]',
      title: 'Reachout',
      description: 'Multi-channel outreach engine. Send personalised emails, WhatsApp messages, and voice calls to candidates. Build cadences and track opens, replies, and responses.',
      icon: '📨',
    },
    {
      target: '[data-testid="nav-clients"]',
      title: 'Clients',
      description: 'Manage your hiring clients. Each client has a portal where they can view shortlisted candidates and give feedback. Keep everything transparent.',
      icon: '🏢',
    },
    {
      target: '[data-testid="nav-interviews"]',
      title: 'Interviews',
      description: 'Schedule and track interviews across all jobs. See what\'s coming up this week, and keep notes on each interview round.',
      icon: '📅',
    },
    {
      target: '[data-testid="nav-analytics"]',
      title: 'Analytics',
      description: 'Deep performance reports. Track time-to-hire, pipeline conversion rates, recruiter efficiency, and revenue. Shareable with stakeholders.',
      icon: '📈',
    },
    {
      target: '[data-testid="nav-performance"]',
      title: 'Performance',
      description: 'Monitor your entire team\'s performance. See who\'s hitting targets, review individual recruiter stats, and manage team structure.',
      icon: '👨‍💼',
    },
    {
      target: '[data-testid="nav-daily-targets"]',
      title: 'Daily Targets',
      description: 'Set and track daily submission targets for every recruiter, per job. Copy previous day\'s targets, bulk-set them, and see live progress throughout the day.',
      icon: '🎯',
    },
    {
      target: '[data-testid="nav-users"]',
      title: 'User Management',
      description: 'Add, edit, and manage your team accounts. Assign roles (Admin, Manager, Recruiter) and set permissions. Create accounts for new hires in seconds.',
      icon: '⚙️',
    },
    {
      target: '[data-testid="nav-ats-settings"]',
      title: 'ATS Settings',
      description: 'Configure your pipeline stages, email templates, integrations (ElevenLabs, WhatsApp, Telephony), and company branding. Make HiroJet OS yours.',
      icon: '🔧',
    },
    {
      target: null,
      title: 'You\'re All Set!',
      description: 'You\'ve seen all the major features of HiroJet OS. Explore at your own pace, and visit the Help Centre anytime from the sidebar for detailed guides per feature.',
      icon: '🎉',
    },
  ],

  manager: [
    {
      target: null,
      title: 'Welcome, Manager!',
      description: 'As a Manager, you oversee your team of recruiters, set targets, and manage client relationships. Here\'s a quick tour of your key workflows.',
      icon: '🌟',
    },
    {
      target: '[data-testid="nav-dashboard"]',
      title: 'Your Team Dashboard',
      description: 'See your team\'s combined performance — active jobs, candidates in pipeline, upcoming interviews, and target progress. A great way to start each day.',
      icon: '📊',
    },
    {
      target: '[data-testid="nav-jobs"]',
      title: 'Managing Jobs',
      description: 'Create job listings for your clients, assign them to specific recruiters, and monitor each job\'s pipeline. You have full edit access to all jobs.',
      icon: '💼',
    },
    {
      target: '[data-testid="nav-talent-hub"]',
      title: 'Talent Hub',
      description: 'Browse and manage all candidates uploaded by your team. Upload resumes in bulk, view full profiles, and add candidates to any job pipeline.',
      icon: '👥',
    },
    {
      target: '[data-testid="nav-ai-recruiter"]',
      title: 'AI Recruiter',
      description: 'Use AI to quickly screen, rank, and shortlist candidates against job requirements. Save your team hours of manual screening every day.',
      icon: '🤖',
    },
    {
      target: '[data-testid="nav-reachout"]',
      title: 'Reachout',
      description: 'Send personalised outreach to candidates via email, WhatsApp, and voice. Build automated cadences so no candidate falls through the cracks.',
      icon: '📨',
    },
    {
      target: '[data-testid="nav-clients"]',
      title: 'Client Management',
      description: 'Manage your hiring clients. Share candidate shortlists via the Client Portal and collect feedback without endless email chains.',
      icon: '🏢',
    },
    {
      target: '[data-testid="nav-performance"]',
      title: 'Performance',
      description: 'See your recruiters\' individual performance — submissions, shortlists, hires. Identify who needs support and who\'s excelling.',
      icon: '👨‍💼',
    },
    {
      target: '[data-testid="nav-daily-targets"]',
      title: 'Daily Targets',
      description: 'Set daily submission targets for each recruiter on specific jobs. Targets reset daily — copy the previous day or use Bulk Set to update everyone at once.',
      icon: '🎯',
    },
    {
      target: '[data-testid="nav-interviews"]',
      title: 'Interviews',
      description: 'Track all upcoming interviews for your team. Make sure candidates are properly prepared and no interview slots are missed.',
      icon: '📅',
    },
    {
      target: null,
      title: 'You\'re Ready!',
      description: 'That covers your main workflows as a Manager. Remember — your recruiters can only see candidates in their assigned jobs. Visit the Help Centre for detailed guides.',
      icon: '✅',
    },
  ],

  recruiter: [
    {
      target: null,
      title: 'Welcome to HiroJet OS!',
      description: 'As a Recruiter, your job is to find the best candidates and move them through the hiring pipeline. Here\'s a quick tour of your key tools.',
      icon: '🎯',
    },
    {
      target: '[data-testid="nav-dashboard"]',
      title: 'Your Dashboard',
      description: 'Your personal performance hub. See your daily targets set by your manager, track your submissions and shortlists, and stay motivated with your streaks.',
      icon: '📊',
    },
    {
      target: '[data-testid="nav-jobs"]',
      title: 'Your Jobs',
      description: 'See all open positions you\'re assigned to. Click any job to open its pipeline — your Kanban board for tracking candidates from Internal Screening to Joined.',
      icon: '💼',
    },
    {
      target: '[data-testid="nav-talent-hub"]',
      title: 'Talent Hub',
      description: 'Your candidate database. Upload resumes (AI parses them automatically!), search by skills, and manage candidate profiles. Only your own candidates are shown here.',
      icon: '👥',
    },
    {
      target: '[data-testid="nav-ai-recruiter"]',
      title: 'AI Recruiter',
      description: 'Your secret weapon. Select a job, pick a screening mode, and let AI evaluate candidates against the job requirements in seconds. Saves hours of manual work.',
      icon: '🤖',
    },
    {
      target: '[data-testid="nav-reachout"]',
      title: 'Reachout',
      description: 'Reach candidates where they are. Send personalised emails or WhatsApp messages to multiple candidates at once. Track opens and replies in real-time.',
      icon: '📨',
    },
    {
      target: '[data-testid="nav-interviews"]',
      title: 'Interviews',
      description: 'Keep track of upcoming interviews for your candidates. Add interview notes and feedback to keep your manager in the loop.',
      icon: '📅',
    },
    {
      target: '[data-testid="nav-daily-targets"]',
      title: 'Daily Targets',
      description: 'See the daily submission targets your manager has set for you. Track your progress throughout the day and aim to hit or beat your targets!',
      icon: '🎯',
    },
    {
      target: null,
      title: 'You\'re Ready to Recruit!',
      description: 'That\'s your core toolkit. Start by going to Jobs and clicking on an assigned position to add your first candidate. Good luck! 🚀',
      icon: '🏆',
    },
  ],
};

/* ─── Tooltip positioning helper ────────────────────────── */
function getTooltipStyle(rect, side) {
  const pad = 16;
  const tooltipW = 360;
  const tooltipH = 220;

  if (!rect) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: tooltipW,
    };
  }

  // Choose best side if 'auto'
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let resolvedSide = side;
  if (side === 'auto' || !side) {
    const spaceRight = vw - rect.right - pad;
    const spaceLeft = rect.left - pad;
    const spaceBottom = vh - rect.bottom - pad;
    const spaceTop = rect.top - pad;

    if (spaceRight >= tooltipW) resolvedSide = 'right';
    else if (spaceLeft >= tooltipW) resolvedSide = 'left';
    else if (spaceBottom >= tooltipH) resolvedSide = 'bottom';
    else resolvedSide = 'top';
  }

  const center = rect.top + rect.height / 2;
  const hCenter = rect.left + rect.width / 2;

  switch (resolvedSide) {
    case 'right':
      return {
        position: 'fixed',
        top: Math.max(pad, Math.min(vh - tooltipH - pad, center - tooltipH / 2)),
        left: rect.right + pad,
        width: tooltipW,
      };
    case 'left':
      return {
        position: 'fixed',
        top: Math.max(pad, Math.min(vh - tooltipH - pad, center - tooltipH / 2)),
        left: rect.left - tooltipW - pad,
        width: tooltipW,
      };
    case 'bottom':
      return {
        position: 'fixed',
        top: rect.bottom + pad,
        left: Math.max(pad, Math.min(vw - tooltipW - pad, hCenter - tooltipW / 2)),
        width: tooltipW,
      };
    case 'top':
    default:
      return {
        position: 'fixed',
        top: rect.top - tooltipH - pad,
        left: Math.max(pad, Math.min(vw - tooltipW - pad, hCenter - tooltipW / 2)),
        width: tooltipW,
      };
  }
}

/* ─── Main component ─────────────────────────────────────── */
export function GuidedTour() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const rafRef = useRef(null);
  const hasLaunchedRef = useRef(false);

  const role = user?.role || 'recruiter';
  const steps = TOUR_STEPS[role] || TOUR_STEPS.recruiter;
  const storageKey = `hirojet-tour-v1-${user?.id}-${role}`;

  // Auto-launch for new users — runs once per mount, persists skip in localStorage
  useEffect(() => {
    if (!user?.id || !TOUR_STEPS[role] || hasLaunchedRef.current) return;
    const seen = localStorage.getItem(storageKey);
    const skipPages = ['/app/ai-calling'];
    if (!seen && !skipPages.some(p => window.location.pathname.startsWith(p))) {
      const t = setTimeout(() => { hasLaunchedRef.current = true; setIsOpen(true); }, 1200);
      return () => clearTimeout(t);
    }
  }, [user?.id, storageKey]);

  // Listen for manual trigger from HelpCenterPage
  useEffect(() => {
    const handler = () => {
      setStep(0);
      setIsOpen(true);
    };
    window.addEventListener('hirojet-start-tour', handler);
    return () => window.removeEventListener('hirojet-start-tour', handler);
  }, []);

  // Update highlight rect on step change + scroll target into view
  useEffect(() => {
    if (!isOpen) return;
    const current = steps[step];
    if (!current?.target) {
      setHighlightRect(null);
      return;
    }

    const updateRect = () => {
      const el = document.querySelector(current.target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        rafRef.current = requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          setHighlightRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        });
      } else {
        setHighlightRect(null);
      }
    };

    const t = setTimeout(updateRect, 300);
    return () => {
      clearTimeout(t);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [step, isOpen, steps]);

  const close = useCallback((completed = false) => {
    setIsOpen(false);
    setStep(0);
    setHighlightRect(null);
    if (completed || true) {
      // Mark as seen regardless of skip/complete
      localStorage.setItem(storageKey, 'done');
    }
  }, [storageKey]);

  const next = () => {
    if (step < steps.length - 1) setStep(s => s + 1);
    else close(true);
  };

  const prev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  if (!isOpen) return null;

  const current = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const tooltipStyle = getTooltipStyle(highlightRect);

  const GLOW = 8; // px padding around highlight
  const hr = highlightRect;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] pointer-events-all"
        style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(2px)' }}
        onClick={() => close(false)}
        data-testid="tour-backdrop"
      />

      {/* Highlight cutout — rendered on top of backdrop */}
      {hr && (
        <div
          className="fixed z-[9999] pointer-events-none rounded-xl"
          style={{
            top: hr.top - GLOW,
            left: hr.left - GLOW,
            width: hr.width + GLOW * 2,
            height: hr.height + GLOW * 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
            border: '2px solid rgba(99,235,188,0.7)',
            background: 'transparent',
          }}
          data-testid="tour-highlight"
        />
      )}

      {/* Tooltip card */}
      <div
        className="z-[10000] rounded-xl p-4 shadow-2xl border border-white/[0.08]"
        style={{
          ...tooltipStyle,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        }}
        data-testid="tour-tooltip"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{current.icon}</span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-400 mb-1">
                Step {step + 1} of {steps.length}
              </p>
              <h3 className="text-white font-bold text-base leading-tight">{current.title}</h3>
            </div>
          </div>
          <button
            onClick={() => close(false)}
            className="text-slate-500 hover:text-white transition-colors mt-1 flex-shrink-0"
            data-testid="tour-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Description */}
        <p className="text-slate-300 text-sm leading-relaxed mb-4">{current.description}</p>

        {/* Progress dots */}
        <div className="flex items-center gap-1 mb-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === step ? 20 : 6,
                height: 6,
                background: i === step ? '#34D399' : i < step ? '#6EE7B7' : 'rgba(255,255,255,0.15)',
              }}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => close(false)}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {!isFirst && (
              <Button
                variant="ghost"
                size="sm"
                onClick={prev}
                className="h-8 px-3 text-slate-400 hover:text-white text-xs"
                data-testid="tour-prev"
              >
                <ArrowLeft className="w-3 h-3 mr-1" /> Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={next}
              className="h-8 px-4 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-xs"
              data-testid="tour-next"
            >
              {isLast ? (
                <>Done <Sparkles className="w-3 h-3 ml-1" /></>
              ) : (
                <>Next <ArrowRight className="w-3 h-3 ml-1" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
